import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-reimagined/components/layout';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq } from 'tio-ui/utilities';
import ActionableCard from 'tio-reimagined/components/actionable-card';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import StatCard from 'tio-reimagined/components/stat-card';
import PageHero from 'tio-reimagined/components/page-hero';
import {
  PhArrowLeft,
  PhHeart,
  PhShare,
  PhTarget,
  PhBriefcase,
  PhCurrencyDollar,
  PhTrendUp,
  PhBuildings,
  PhGraduationCap,
  PhCertificate,
  PhClock,
  PhCheckCircle,
  PhWarning,
  PhLightbulb,
  PhUsers,
  PhArrowRight,
  PhSparkle,
  PhStar,
  PhRocket,
  PhCalendar,
  PhMapPin,
  PhBookOpen,
  PhRobot,
  PhPath,
} from 'ember-phosphor-icons';

// Mock detailed role data (Nurse Manager)
const HEALTHCARE_ROLE_DATA = {
  id: 'nurse-manager',
  title: 'Nurse Manager',
  family: 'Nursing',
  level: 'Senior Management',
  matchScore: 92,

  overview: {
    description:
      'Nurse Managers oversee nursing staff in hospital units, ensuring quality patient care while managing budgets, schedules, and team development. This role bridges clinical expertise with administrative leadership.',
    dayInLife: [
      'Conduct morning rounds and team huddles',
      'Review patient care metrics and quality indicators',
      'Manage staffing schedules and handle call-outs',
      'Meet with interdisciplinary teams',
      'Coach and mentor nursing staff',
      'Participate in budget planning',
      'Address patient and family concerns',
      'Ensure compliance with regulatory standards',
    ],
  },

  compensation: {
    salaryRange: '$85,000 - $120,000',
    median: '$102,500',
    percentile25: '$88,000',
    percentile75: '$115,000',
    bonusRange: '$5,000 - $15,000',
    totalComp: '$90,000 - $135,000',
    benefits: ['Health/Dental/Vision', '401k Match (6%)', 'Tuition Assistance', 'PTO (25 days)'],
  },

  demand: {
    growthRate: '+18%',
    growthPeriod: '10-year outlook',
    openingsNational: 25400,
    openingsLocal: 340,
    openingsCompany: 8,
    retirementWave: 'High - 35% of current managers retiring within 5 years',
    demandLevel: 'Very High',
  },

  requirements: {
    education: [
      { name: 'BSN Required', status: 'completed', yours: 'Completed Aug 2024' },
      { name: 'MSN Preferred', status: 'optional', yours: 'Not started' },
    ],
    certifications: [
      { name: 'RN License', status: 'completed', yours: 'Active' },
      { name: 'BLS/ACLS', status: 'completed', yours: 'Current' },
      {
        name: 'Nurse Leadership Certification',
        status: 'in-progress',
        yours: '65% complete',
        recommended: true,
      },
    ],
    experience: [
      { name: '3-5 years bedside nursing', status: 'completed', yours: '4 years' },
      { name: 'Charge nurse experience', status: 'needed', yours: 'Not yet' },
      { name: 'Staff supervision', status: 'partial', yours: 'Limited (precepting)' },
    ],
  },

  skills: {
    matching: [
      { name: 'Patient Care', level: 95, status: 'strong' },
      { name: 'Clinical Assessment', level: 90, status: 'strong' },
      { name: 'Team Collaboration', level: 85, status: 'strong' },
      { name: 'Epic EMR', level: 80, status: 'strong' },
      { name: 'Critical Thinking', level: 88, status: 'strong' },
    ],
    developing: [
      { name: 'Leadership', level: 65, status: 'developing' },
      { name: 'Staff Development', level: 40, status: 'developing' },
    ],
    gaps: [
      { name: 'Budget Management', level: 15, status: 'gap', criticality: 'High' },
      { name: 'Staff Scheduling', level: 20, status: 'gap', criticality: 'High' },
      { name: 'Quality Improvement', level: 30, status: 'gap', criticality: 'Medium' },
    ],
  },

  careerLadder: [
    { title: 'Staff RN', level: 1, salary: '$55-72k', isCurrent: false, isPast: true },
    { title: 'Senior RN', level: 2, salary: '$65-85k', isCurrent: false, isPast: true },
    { title: 'Charge Nurse', level: 3, salary: '$72-95k', isCurrent: false, isPast: false },
    { title: 'Nurse Manager', level: 4, salary: '$85-120k', isCurrent: false, isTarget: true },
    {
      title: 'Director of Nursing',
      level: 5,
      salary: '$110-145k',
      isCurrent: false,
      isPast: false,
    },
    { title: 'CNO', level: 6, salary: '$150-250k', isCurrent: false, isPast: false },
  ],

  companyOpenings: [
    {
      title: 'Nurse Manager - ICU',
      department: 'Critical Care',
      location: 'Main Campus',
      posted: '3 days ago',
      applicants: 12,
    },
    {
      title: 'Nurse Manager - Med-Surg',
      department: 'Medical-Surgical',
      location: 'Main Campus',
      posted: '1 week ago',
      applicants: 18,
    },
    {
      title: 'Nurse Manager - Emergency',
      department: 'Emergency Services',
      location: 'West Campus',
      posted: '2 weeks ago',
      applicants: 24,
    },
  ],

  learningPaths: [
    {
      name: 'Fast Track to Nurse Manager',
      duration: '12 months',
      courses: 6,
      provider: 'Internal + ANCC',
      tuitionCovered: true,
      recommended: true,
    },
    {
      name: 'MSN in Nursing Leadership',
      duration: '24 months',
      courses: 12,
      provider: 'Partner University',
      tuitionCovered: true,
      recommended: false,
    },
    {
      name: 'Healthcare Management Certificate',
      duration: '6 months',
      courses: 4,
      provider: 'Coursera / Google',
      tuitionCovered: true,
      recommended: false,
    },
  ],

  successStories: [
    {
      name: 'Maria S.',
      journey: 'Staff RN → Nurse Manager in 3 years',
      quote: 'The leadership certificate program was a game-changer. I felt prepared from day one.',
      avatar: '👩‍⚕️',
    },
    {
      name: 'James T.',
      journey: 'Charge Nurse → Nurse Manager in 18 months',
      quote: "My manager's support and the mentorship program made the transition smooth.",
      avatar: '👨‍⚕️',
    },
  ],
};

const UPS_ROLE_DATA = {
  id: 'hub-supervisor',
  title: 'Hub Supervisor',
  family: 'Operations Leadership',
  level: 'Management',
  matchScore: 84,

  overview: {
    description:
      'Hub Supervisors lead package operations teams, manage safety and quality standards, and coordinate staffing to meet daily volume targets. The role blends frontline leadership with process improvement.',
    dayInLife: [
      'Lead shift kickoff and safety briefings',
      'Monitor volume, throughput, and service metrics',
      'Coordinate staffing adjustments with team leads',
      'Coach supervisors and address performance needs',
      'Partner with maintenance and dispatch teams',
      'Review incident reports and compliance logs',
      'Communicate status to management and peers',
    ],
  },

  compensation: {
    salaryRange: '$68,000 - $92,000',
    median: '$80,000',
    percentile25: '$72,000',
    percentile75: '$88,000',
    bonusRange: '$4,000 - $10,000',
    totalComp: '$72,000 - $102,000',
    benefits: ['Health/Dental/Vision', '401k Match (6%)', 'Tuition Assistance', 'PTO (20 days)'],
  },

  demand: {
    growthRate: '+12%',
    growthPeriod: '5-year outlook',
    openingsNational: 14800,
    openingsLocal: 120,
    openingsCompany: 18,
    retirementWave: 'Medium - 20% of supervisors retiring within 5 years',
    demandLevel: 'High',
  },

  requirements: {
    education: [
      { name: 'High school diploma', status: 'completed', yours: 'Completed' },
      { name: 'Associate degree preferred', status: 'optional', yours: 'In progress' },
    ],
    certifications: [
      { name: 'Safety Leadership Training', status: 'completed', yours: 'Current' },
      {
        name: 'OSHA Compliance Certificate',
        status: 'in-progress',
        yours: '45% complete',
        recommended: true,
      },
    ],
    experience: [
      { name: '2+ years operations experience', status: 'completed', yours: '3 years' },
      { name: 'Team lead experience', status: 'partial', yours: '6 months' },
      { name: 'Shift scheduling', status: 'needed', yours: 'Not yet' },
    ],
  },

  skills: {
    matching: [
      { name: 'Package Handling Ops', level: 90, status: 'strong' },
      { name: 'Safety Compliance', level: 82, status: 'strong' },
      { name: 'Team Collaboration', level: 85, status: 'strong' },
      { name: 'Process Discipline', level: 78, status: 'strong' },
    ],
    developing: [
      { name: 'Leadership', level: 62, status: 'developing' },
      { name: 'Performance Coaching', level: 48, status: 'developing' },
    ],
    gaps: [
      { name: 'Staff Scheduling', level: 20, status: 'gap', criticality: 'High' },
      { name: 'Cost Management', level: 25, status: 'gap', criticality: 'Medium' },
      { name: 'Continuous Improvement', level: 35, status: 'gap', criticality: 'Medium' },
    ],
  },

  careerLadder: [
    { title: 'Package Handler', level: 1, salary: '$38-45k', isCurrent: false, isPast: true },
    { title: 'Team Lead', level: 2, salary: '$45-55k', isCurrent: false, isPast: true },
    { title: 'Shift Supervisor', level: 3, salary: '$55-70k', isCurrent: false, isPast: false },
    { title: 'Hub Supervisor', level: 4, salary: '$68-92k', isCurrent: false, isTarget: true },
    { title: 'Operations Manager', level: 5, salary: '$85-115k', isCurrent: false, isPast: false },
    { title: 'Hub Manager', level: 6, salary: '$110-150k', isCurrent: false, isPast: false },
  ],

  companyOpenings: [
    {
      title: 'Hub Supervisor - Twilight Shift',
      department: 'Operations',
      location: 'Regional Hub',
      posted: '2 days ago',
      applicants: 9,
    },
    {
      title: 'Hub Supervisor - Sort',
      department: 'Sorting',
      location: 'North Hub',
      posted: '5 days ago',
      applicants: 14,
    },
    {
      title: 'Hub Supervisor - Ramp',
      department: 'Ramp',
      location: 'Airport Hub',
      posted: '1 week ago',
      applicants: 11,
    },
  ],

  learningPaths: [
    {
      name: 'Supervisor Leadership Accelerator',
      duration: '9 months',
      courses: 5,
      provider: 'Internal + Coursera',
      tuitionCovered: true,
      recommended: true,
    },
    {
      name: 'Lean Operations Certificate',
      duration: '6 months',
      courses: 4,
      provider: 'Partner University',
      tuitionCovered: true,
      recommended: false,
    },
    {
      name: 'Safety & Compliance Track',
      duration: '4 months',
      courses: 3,
      provider: 'OSHA',
      tuitionCovered: true,
      recommended: false,
    },
  ],

  successStories: [
    {
      name: 'Carlos M.',
      journey: 'Team Lead → Hub Supervisor in 2 years',
      quote: 'The leadership program gave me the confidence to run my own shift.',
      avatar: '👨‍🏭',
    },
    {
      name: 'Tanya R.',
      journey: 'Sorter → Hub Supervisor in 3 years',
      quote: 'Coaching and safety training helped me stand out for promotion.',
      avatar: '👩‍🏭',
    },
  ],
};

const STARBUCKS_ROLE_DATA = {
  id: 'store-manager',
  title: 'Store Manager',
  family: 'Store Leadership',
  level: 'Management',
  matchScore: 88,

  overview: {
    description:
      'Store Managers lead partner teams, drive customer experience, and deliver on store performance goals. The role combines people leadership, operational excellence, and community engagement.',
    dayInLife: [
      'Lead morning huddle and day-part planning',
      'Coach partners on service and quality standards',
      'Manage labor planning and scheduling',
      'Review inventory and ordering with shift leaders',
      'Handle customer escalations and feedback',
      'Partner with district leaders on store goals',
      'Recognize and develop partner growth plans',
    ],
  },

  compensation: {
    salaryRange: '$62,000 - $88,000',
    median: '$75,000',
    percentile25: '$66,000',
    percentile75: '$82,000',
    bonusRange: '$3,000 - $9,000',
    totalComp: '$65,000 - $95,000',
    benefits: ['Health/Dental/Vision', '401k Match (5%)', 'Tuition Assistance', 'PTO (18 days)'],
  },

  demand: {
    growthRate: '+10%',
    growthPeriod: '5-year outlook',
    openingsNational: 18200,
    openingsLocal: 150,
    openingsCompany: 22,
    retirementWave: 'Medium - 18% of managers retiring within 5 years',
    demandLevel: 'High',
  },

  requirements: {
    education: [
      { name: 'High school diploma', status: 'completed', yours: 'Completed' },
      { name: 'Associate degree preferred', status: 'optional', yours: 'Not started' },
    ],
    certifications: [
      { name: 'Food Safety Certification', status: 'completed', yours: 'Current' },
      {
        name: 'Store Manager Development',
        status: 'in-progress',
        yours: '55% complete',
        recommended: true,
      },
    ],
    experience: [
      { name: '2+ years retail experience', status: 'completed', yours: '3 years' },
      { name: 'Shift Supervisor experience', status: 'completed', yours: '1 year' },
      { name: 'Labor scheduling', status: 'partial', yours: 'Limited' },
    ],
  },

  skills: {
    matching: [
      { name: 'Customer Experience', level: 92, status: 'strong' },
      { name: 'Team Collaboration', level: 88, status: 'strong' },
      { name: 'Quality Standards', level: 84, status: 'strong' },
      { name: 'Retail Operations', level: 80, status: 'strong' },
    ],
    developing: [
      { name: 'Financial Management', level: 60, status: 'developing' },
      { name: 'Talent Development', level: 52, status: 'developing' },
    ],
    gaps: [
      { name: 'Labor Forecasting', level: 22, status: 'gap', criticality: 'High' },
      { name: 'Inventory Management', level: 28, status: 'gap', criticality: 'Medium' },
      { name: 'Multi-unit Leadership', level: 30, status: 'gap', criticality: 'Medium' },
    ],
  },

  careerLadder: [
    { title: 'Barista', level: 1, salary: '$30-38k', isCurrent: false, isPast: true },
    { title: 'Shift Supervisor', level: 2, salary: '$38-48k', isCurrent: false, isPast: true },
    {
      title: 'Assistant Store Manager',
      level: 3,
      salary: '$48-60k',
      isCurrent: false,
      isPast: false,
    },
    { title: 'Store Manager', level: 4, salary: '$62-88k', isCurrent: false, isTarget: true },
    { title: 'District Manager', level: 5, salary: '$90-120k', isCurrent: false, isPast: false },
    { title: 'Regional Director', level: 6, salary: '$120-160k', isCurrent: false, isPast: false },
  ],

  companyOpenings: [
    {
      title: 'Store Manager - Downtown',
      department: 'Retail',
      location: 'City Center',
      posted: '1 day ago',
      applicants: 16,
    },
    {
      title: 'Store Manager - Campus',
      department: 'Retail',
      location: 'University District',
      posted: '6 days ago',
      applicants: 12,
    },
    {
      title: 'Store Manager - Airport',
      department: 'Retail',
      location: 'Airport',
      posted: '2 weeks ago',
      applicants: 20,
    },
  ],

  learningPaths: [
    {
      name: 'Store Manager Launch',
      duration: '12 months',
      courses: 6,
      provider: 'Internal + ASU',
      tuitionCovered: true,
      recommended: true,
    },
    {
      name: 'Retail Operations Certificate',
      duration: '8 months',
      courses: 5,
      provider: 'Partner University',
      tuitionCovered: true,
      recommended: false,
    },
    {
      name: 'Coffee Leadership Track',
      duration: '6 months',
      courses: 4,
      provider: 'Internal',
      tuitionCovered: true,
      recommended: false,
    },
  ],

  successStories: [
    {
      name: 'Avery L.',
      journey: 'Shift Supervisor → Store Manager in 2 years',
      quote: 'The development program helped me lead with confidence.',
      avatar: '👩‍💼',
    },
    {
      name: 'Jordan K.',
      journey: 'Barista → Store Manager in 3 years',
      quote: 'Coaching and tuition support opened doors for me.',
      avatar: '🧑‍💼',
    },
  ],
};

export default class CareerPathwaysExploreRole extends Component {
  @tracked activeTab = 'overview';
  @tracked isSaved = false;

  get roleData() {
    const clientId = getCurrentDemoClientData().client.id;

    if (clientId === 'ups') {
      return UPS_ROLE_DATA;
    }

    if (clientId === 'starbucks') {
      return STARBUCKS_ROLE_DATA;
    }

    return HEALTHCARE_ROLE_DATA;
  }

  tabs = [
    { id: 'overview', name: 'Overview', icon: PhBriefcase },
    { id: 'skills', name: 'Skills Match', icon: PhLightbulb },
    { id: 'path', name: 'Career Path', icon: PhPath },
    { id: 'learning', name: 'Learning', icon: PhGraduationCap },
    { id: 'openings', name: 'Open Positions', icon: PhBuildings },
  ];

  setTab = (tabId: string) => {
    this.activeTab = tabId;
  };

  toggleSaved = () => {
    this.isSaved = !this.isSaved;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Back Navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined.explore"
          class="tio-link-brand-inline tio-inline-row gap-2 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Explore Careers
        </LinkTo>
      </div>

      {{! Hero Section }}
      <PageHero @gradient="brand">
        <:default>
          <div class="tio-row-start-between">
            <div class="flex-1">
              <div class="tio-row mb-2">
                <Chip @intent="warning" class="tio-bg-warning tio-fg-warning-h">
                  <span class="tio-inline-row gap-1">
                    <PhTrendUp @size={{14}} />
                    High Demand
                  </span>
                </Chip>
                <Chip class="bg-white/20 tio-fg-inverse">{{this.roleData.family}}</Chip>
                <Chip class="bg-white/20 tio-fg-inverse">{{this.roleData.level}}</Chip>
              </div>
              <Heading @level={{1}} class="tio-fg-inverse text-4xl font-bold mb-2">
                {{this.roleData.title}}
              </Heading>
              <p class="tio-fg-on-brand text-lg mb-6 max-w-2xl">
                {{this.roleData.overview.description}}
              </p>

              <div class="flex flex-wrap gap-6 text-sm">
                <div class="tio-row-tight">
                  <PhCurrencyDollar @size={{20}} class="text-green-300" />
                  <span class="font-semibold">{{this.roleData.compensation.salaryRange}}</span>
                </div>
                <div class="tio-row-tight">
                  <PhTrendUp @size={{20}} class="text-green-300" />
                  <span>{{this.roleData.demand.growthRate}} growth</span>
                </div>
                <div class="tio-row-tight">
                  <PhBuildings @size={{20}} class="tio-fg-on-brand" />
                  <span>{{this.roleData.demand.openingsCompany}} openings at your company</span>
                </div>
              </div>
            </div>

            {{! Match Score Card }}
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center ml-6">
              <div
                class="w-28 h-28 rounded-full mx-auto mb-3 tio-row-center tio-bg-success border-4 tio-border-success"
              >
                <span class="text-4xl font-bold tio-fg-inverse">{{this.roleData.matchScore}}%</span>
              </div>
              <Text @weight="semibold" class="tio-fg-inverse mb-1">Excellent Match!</Text>
              <p class="tio-fg-on-brand text-sm mb-4">Based on your skills & experience</p>
              <div class="tio-flex-tight">
                <Button
                  @intent="default"
                  class="tio-bg-surface tio-fg-brand hover:bg-[var(--brand-wash)]"
                  {{on "click" this.toggleSaved}}
                >
                  <PhHeart
                    @size={{18}}
                    @weight={{if this.isSaved "fill" "regular"}}
                    class="{{if this.isSaved 'tio-fg-danger'}}"
                  />
                </Button>
                <Button
                  @intent="default"
                  class="tio-bg-surface tio-fg-brand hover:bg-[var(--brand-wash)]"
                >
                  <PhShare @size={{18}} />
                </Button>
              </div>
            </div>
          </div>
        </:default>
      </PageHero>

      {{! Action Bar }}
      <div class="tio-bg-surface rounded-lg shadow-sm p-4 mb-6 tio-row-between">
        <div class="tio-row-loose">
          <Button @intent="primary" @size="lg" @icon={{PhTarget}} @label="Set as Career Goal" />
          <Button
            @intent="primary"
            @appearance="outlined"
            @icon={{PhRobot}}
            @label="Ask AI Advisor About This Role"
          />
        </div>
        <div class="tio-row text-sm tio-fg-muted">
          <PhUsers @size={{18}} />
          <span>847 employees exploring this role</span>
        </div>
      </div>

      {{! Tab Navigation }}
      <div class="tio-bg-surface rounded-lg shadow-sm mb-6">
        <div class="flex border-b tio-border-default">
          {{#each this.tabs as |tab|}}
            <button
              type="button"
              {{on "click" (fn this.setTab tab.id)}}
              class="tio-row-tight px-6 py-4 text-sm font-medium border-b-2 transition-colors
                {{if
                  (eq this.activeTab tab.id)
                  'tio-border-brand tio-fg-brand'
                  'border-transparent tio-fg-muted hover:tio-fg-secondary hover:tio-border-strong'
                }}"
            >
              {{#let tab.icon as |Icon|}}
                <Icon @size={{18}} />
              {{/let}}
              {{tab.name}}
            </button>
          {{/each}}
        </div>

        {{! Tab Content }}
        <div class="p-6">
          {{#if (eq this.activeTab "overview")}}
            {{! Overview Tab }}
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {{! Day in the Life }}
              <Section class="tio-bg-sunken rounded-lg">
                <:header>
                  <div class="tio-row-tight">
                    <PhCalendar @size={{20}} class="tio-fg-brand" />
                    <span>A Day in the Life</span>
                  </div>
                </:header>
                <:body>
                  <ul class="space-y-3">
                    {{#each this.roleData.overview.dayInLife as |activity|}}
                      <li class="tio-row-top">
                        <PhCheckCircle @size={{18}} class="tio-fg-success mt-0.5 shrink-0" />
                        <span class="tio-fg-secondary">{{activity}}</span>
                      </li>
                    {{/each}}
                  </ul>
                </:body>
              </Section>

              {{! Compensation }}
              <Section class="tio-bg-sunken rounded-lg">
                <:header>
                  <div class="tio-row-tight">
                    <PhCurrencyDollar @size={{20}} class="tio-fg-success" />
                    <span>Compensation Details</span>
                  </div>
                </:header>
                <:body>
                  <div class="space-y-4">
                    <div class="tio-bg-surface p-4 rounded-lg">
                      <Text @variant="muted" @size="sm">Base Salary Range</Text>
                      <div class="text-2xl font-bold tio-fg-primary">
                        {{this.roleData.compensation.salaryRange}}
                      </div>
                      <div class="tio-flex-between text-sm mt-2">
                        <span class="tio-fg-muted">25th:
                          {{this.roleData.compensation.percentile25}}</span>
                        <span class="font-medium">Median:
                          {{this.roleData.compensation.median}}</span>
                        <span class="tio-fg-muted">75th:
                          {{this.roleData.compensation.percentile75}}</span>
                      </div>
                      <ProgressBar @value={{65}} @color="emerald" class="mt-2" />
                    </div>

                    <div class="tio-flex-loose">
                      <div class="flex-1 tio-bg-surface p-3 rounded-lg text-center">
                        <Text @variant="muted" @size="sm">Annual Bonus</Text>
                        <div
                          class="font-bold tio-fg-primary"
                        >{{this.roleData.compensation.bonusRange}}</div>
                      </div>
                      <div class="flex-1 tio-bg-surface p-3 rounded-lg text-center">
                        <Text @variant="muted" @size="sm">Total Comp</Text>
                        <div
                          class="font-bold tio-fg-success"
                        >{{this.roleData.compensation.totalComp}}</div>
                      </div>
                    </div>

                    <div>
                      <Text @variant="muted" @size="sm" class="mb-2">Benefits Include:</Text>
                      <div class="tio-wrap-tight">
                        {{#each this.roleData.compensation.benefits as |benefit|}}
                          <Chip @intent="default">{{benefit}}</Chip>
                        {{/each}}
                      </div>
                    </div>
                  </div>
                </:body>
              </Section>

              {{! Requirements }}
              <Section class="tio-bg-sunken rounded-lg lg:col-span-2">
                <:header>
                  <div class="tio-row-tight">
                    <PhCertificate @size={{20}} class="tio-fg-brand" />
                    <span>Requirements & Your Progress</span>
                  </div>
                </:header>
                <:body>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {{! Education }}
                    <div>
                      <Text @size="sm" @weight="semibold" class="mb-3 tio-row-tight">
                        <PhGraduationCap @size={{16}} />
                        Education
                      </Text>
                      <div class="space-y-2">
                        {{#each this.roleData.requirements.education as |req|}}
                          <div
                            class="p-3 rounded-lg border
                              {{if
                                (eq req.status 'completed')
                                'tio-bg-success-soft tio-border-success'
                                'tio-bg-sunken tio-border-default'
                              }}"
                          >
                            <div class="tio-row-tight mb-1">
                              {{#if (eq req.status "completed")}}
                                <PhCheckCircle @size={{16}} class="tio-fg-success" />
                              {{else}}
                                <PhClock @size={{16}} class="tio-fg-disabled" />
                              {{/if}}
                              <span class="font-medium text-sm">{{req.name}}</span>
                            </div>
                            <Text @variant="muted" @size="xs">{{req.yours}}</Text>
                          </div>
                        {{/each}}
                      </div>
                    </div>

                    {{! Certifications }}
                    <div>
                      <Text @size="sm" @weight="semibold" class="mb-3 tio-row-tight">
                        <PhCertificate @size={{16}} />
                        Certifications
                      </Text>
                      <div class="space-y-2">
                        {{#each this.roleData.requirements.certifications as |req|}}
                          <div
                            class="p-3 rounded-lg border
                              {{if
                                (eq req.status 'completed')
                                'tio-bg-success-soft tio-border-success'
                                (if
                                  (eq req.status 'in-progress')
                                  'tio-bg-brand-wash tio-border-brand-soft'
                                  'tio-bg-sunken tio-border-default'
                                )
                              }}"
                          >
                            <div class="tio-row-tight mb-1">
                              {{#if (eq req.status "completed")}}
                                <PhCheckCircle @size={{16}} class="tio-fg-success" />
                              {{else if (eq req.status "in-progress")}}
                                <PhClock @size={{16}} class="tio-fg-brand" />
                              {{else}}
                                <PhClock @size={{16}} class="tio-fg-disabled" />
                              {{/if}}
                              <span class="font-medium text-sm">{{req.name}}</span>
                              {{#if req.recommended}}
                                <PhSparkle @size={{14}} class="tio-fg-warning" />
                              {{/if}}
                            </div>
                            <Text @variant="muted" @size="xs">{{req.yours}}</Text>
                          </div>
                        {{/each}}
                      </div>
                    </div>

                    {{! Experience }}
                    <div>
                      <Text @size="sm" @weight="semibold" class="mb-3 tio-row-tight">
                        <PhBriefcase @size={{16}} />
                        Experience
                      </Text>
                      <div class="space-y-2">
                        {{#each this.roleData.requirements.experience as |req|}}
                          <div
                            class="p-3 rounded-lg border
                              {{if
                                (eq req.status 'completed')
                                'tio-bg-success-soft tio-border-success'
                                (if
                                  (eq req.status 'partial')
                                  'tio-bg-warning-soft tio-border-warning'
                                  'tio-bg-danger-soft tio-border-danger'
                                )
                              }}"
                          >
                            <div class="tio-row-tight mb-1">
                              {{#if (eq req.status "completed")}}
                                <PhCheckCircle @size={{16}} class="tio-fg-success" />
                              {{else if (eq req.status "partial")}}
                                <PhWarning @size={{16}} class="tio-fg-warning" />
                              {{else}}
                                <PhWarning @size={{16}} class="tio-fg-danger" />
                              {{/if}}
                              <span class="font-medium text-sm">{{req.name}}</span>
                            </div>
                            <Text @variant="muted" @size="xs">{{req.yours}}</Text>
                          </div>
                        {{/each}}
                      </div>
                    </div>
                  </div>
                </:body>
              </Section>
            </div>

          {{else if (eq this.activeTab "skills")}}
            {{! Skills Match Tab }}
            <div class="space-y-6">
              {{! Skills Summary }}
              <div class="grid grid-cols-3 gap-4">
                <StatCard
                  @icon={{PhCheckCircle}}
                  @label="Strong Skills"
                  @value="5"
                  @color="green"
                />
                <StatCard @icon={{PhTrendUp}} @label="Developing" @value="2" @color="violet" />
                <StatCard @icon={{PhWarning}} @label="Skill Gaps" @value="3" @color="red" />
              </div>

              {{! Skill Details }}
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {{! Strong Skills }}
                <div class="tio-bg-success-soft rounded-lg p-6">
                  <Heading @level={{4}} class="tio-row-tight mb-4">
                    <PhCheckCircle @size={{20}} class="tio-fg-success" />
                    Your Strengths
                  </Heading>
                  <div class="space-y-4">
                    {{#each this.roleData.skills.matching as |skill|}}
                      <div>
                        <div class="tio-flex-between mb-1">
                          <span class="font-medium">{{skill.name}}</span>
                          <span class="tio-fg-success font-semibold">{{skill.level}}%</span>
                        </div>
                        <ProgressBar @value={{skill.level}} @color="emerald" />
                      </div>
                    {{/each}}
                  </div>
                </div>

                {{! Gaps & Developing }}
                <div class="space-y-6">
                  <div class="tio-bg-brand-wash rounded-lg p-6">
                    <Heading @level={{4}} class="tio-row-tight mb-4">
                      <PhTrendUp @size={{20}} class="tio-fg-brand" />
                      Currently Developing
                    </Heading>
                    <div class="space-y-4">
                      {{#each this.roleData.skills.developing as |skill|}}
                        <div>
                          <div class="tio-flex-between mb-1">
                            <span class="font-medium">{{skill.name}}</span>
                            <span class="tio-fg-brand font-semibold">{{skill.level}}%</span>
                          </div>
                          <ProgressBar @value={{skill.level}} @color="blue" />
                        </div>
                      {{/each}}
                    </div>
                  </div>

                  <div class="tio-bg-danger-soft rounded-lg p-6">
                    <Heading @level={{4}} class="tio-row-tight mb-4">
                      <PhWarning @size={{20}} class="tio-fg-danger" />
                      Skill Gaps to Address
                    </Heading>
                    <div class="space-y-4">
                      {{#each this.roleData.skills.gaps as |skill|}}
                        <div>
                          <div class="tio-flex-between mb-1">
                            <span class="font-medium">{{skill.name}}</span>
                            <Chip @intent={{if (eq skill.criticality "High") "danger" "warning"}}>
                              {{skill.criticality}}
                            </Chip>
                          </div>
                          <ProgressBar @value={{skill.level}} @color="red" />
                        </div>
                      {{/each}}
                    </div>
                    <Button
                      @intent="danger"
                      class="w-full mt-4"
                      @icon={{PhRocket}}
                      @label="Start Closing These Gaps"
                    />
                  </div>
                </div>
              </div>
            </div>

          {{else if (eq this.activeTab "path")}}
            {{! Career Path Tab }}
            <div class="space-y-6">
              {{! Career Ladder Visualization }}
              <div class="tio-bg-sunken rounded-lg p-8">
                <Heading @level={{3}} class="mb-6 text-center">Career Progression Path</Heading>
                <div class="relative">
                  {{! Connecting line }}
                  <div
                    class="absolute top-1/2 left-0 right-0 h-1 tio-gradient-brand -translate-y-1/2"
                  ></div>

                  <div class="relative tio-flex-between">
                    {{#each this.roleData.careerLadder as |step|}}
                      <div class="tio-stack-center w-[16%]">
                        <div
                          class="w-16 h-16 rounded-full tio-row-center relative z-10 transition-transform hover:scale-110 cursor-pointer
                            {{if
                              step.isTarget
                              'tio-bg-brand text-white border-4 tio-border-brand shadow-lg'
                              (if
                                step.isPast
                                'tio-bg-success text-white border-4 tio-border-success'
                                'tio-bg-surface tio-fg-secondary border-4 tio-border-strong'
                              )
                            }}"
                        >
                          <span class="text-xl font-bold">{{step.level}}</span>
                        </div>
                        <div class="mt-3 text-center">
                          <div
                            class="font-semibold text-sm
                              {{if step.isTarget 'tio-fg-brand' 'tio-fg-primary'}}"
                          >
                            {{step.title}}
                          </div>
                          <Text @variant="muted" @size="xs">{{step.salary}}</Text>
                          {{#if step.isTarget}}
                            <Chip @intent="primary" class="mt-2">Your Goal</Chip>
                          {{else if step.isPast}}
                            <Chip @intent="success" class="mt-2">✓ Achieved</Chip>
                          {{/if}}
                        </div>
                      </div>
                    {{/each}}
                  </div>
                </div>
              </div>

              {{! Time to Reach }}
              <div class="grid grid-cols-3 gap-4">
                <StatCard
                  @icon={{PhClock}}
                  @label="Months to Goal"
                  @value="18-24"
                  @color="violet"
                />
                <StatCard
                  @icon={{PhCheckCircle}}
                  @label="Requirements Met"
                  @value="4 of 6"
                  @color="green"
                />
                <StatCard
                  @icon={{PhBookOpen}}
                  @label="Courses to Complete"
                  @value="3"
                  @color="amber"
                />
              </div>

              {{! Success Stories }}
              <Section class="tio-bg-surface rounded-lg border tio-border-default">
                <:header>
                  <div class="tio-row-tight">
                    <PhStar @size={{20}} class="tio-fg-warning" />
                    <span>Others Who Made This Journey</span>
                  </div>
                </:header>
                <:body>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {{#each this.roleData.successStories as |story|}}
                      <div class="tio-bg-sunken rounded-lg p-4">
                        <div class="tio-row-top">
                          <div class="text-4xl">{{story.avatar}}</div>
                          <div>
                            <div class="font-semibold">{{story.name}}</div>
                            <Text @variant="muted" @size="sm" class="mb-2">{{story.journey}}</Text>
                            <Text @size="sm" class="italic">"{{story.quote}}"</Text>
                          </div>
                        </div>
                      </div>
                    {{/each}}
                  </div>
                </:body>
              </Section>
            </div>

          {{else if (eq this.activeTab "learning")}}
            {{! Learning Tab }}
            <div class="space-y-6">
              <div class="tio-bg-brand-wash rounded-lg p-6 tio-row-between">
                <div>
                  <Heading @level={{3}}>Tuition Assistance Available</Heading>
                  <Text @variant="muted">Your employer covers up to $5,250/year for approved
                    programs</Text>
                </div>
                <Chip @intent="success">
                  <span class="tio-inline-row gap-1">
                    <PhCheckCircle @size={{14}} />
                    Eligible
                  </span>
                </Chip>
              </div>

              <Heading @level={{4}}>Recommended Learning Paths</Heading>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                {{#each this.roleData.learningPaths as |path|}}
                  <ActionableCard
                    @title={{path.name}}
                    @cardClass={{if
                      path.recommended
                      "bg-white rounded-lg border-2 border-violet-500 shadow-sm overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                      "bg-white rounded-lg border-2 tio-border-default shadow-sm overflow-hidden hover:border-violet-300 hover:shadow-lg transition-all cursor-pointer"
                    }}
                  >
                    <:banner>
                      {{#if path.recommended}}
                        <div class="px-4 py-2">
                          <Chip @intent="primary">
                            <span class="tio-inline-row gap-1">
                              <PhSparkle @size={{14}} />
                              Recommended
                            </span>
                          </Chip>
                        </div>
                      {{/if}}
                    </:banner>
                    <:metadata>
                      <div class="space-y-2 mb-4">
                        <div class="tio-row-tight text-sm tio-fg-secondary">
                          <PhClock @size={{16}} />
                          <span>{{path.duration}}</span>
                        </div>
                        <div class="tio-row-tight text-sm tio-fg-secondary">
                          <PhBookOpen @size={{16}} />
                          <span>{{path.courses}} courses</span>
                        </div>
                        <div class="tio-row-tight text-sm tio-fg-secondary">
                          <PhGraduationCap @size={{16}} />
                          <span>{{path.provider}}</span>
                        </div>
                      </div>
                    </:metadata>
                    <:eligibility>
                      {{#if path.tuitionCovered}}
                        <Chip @intent="success">
                          <span class="tio-inline-row gap-1">
                            <PhCheckCircle @size={{14}} />
                            100% Tuition Covered
                          </span>
                        </Chip>
                      {{/if}}
                    </:eligibility>
                    <:actions>
                      <Button
                        @intent={{if path.recommended "primary" "default"}}
                        @appearance={{unless path.recommended "outlined"}}
                        @icon={{PhArrowRight}}
                        @iconPlacement="end"
                        @label="View Program"
                        class="w-full"
                      />
                    </:actions>
                  </ActionableCard>
                {{/each}}
              </div>
            </div>

          {{else if (eq this.activeTab "openings")}}
            {{! Open Positions Tab }}
            <div class="space-y-6">
              <div class="tio-bg-info-soft rounded-lg p-6 tio-row-between">
                <div>
                  <Heading @level={{3}}>{{this.roleData.demand.openingsCompany}}
                    Open Positions</Heading>
                  <Text @variant="muted">Open roles at your company</Text>
                </div>
                <Button @intent="primary">
                  Set Job Alert
                </Button>
              </div>

              <div class="space-y-4">
                {{#each this.roleData.companyOpenings as |job|}}
                  <div
                    class="tio-bg-surface rounded-lg border tio-border-default p-6 hover:shadow-md transition-shadow"
                  >
                    <div class="tio-row-start-between">
                      <div>
                        <Heading @level={{4}} class="tio-fg-brand mb-1">{{job.title}}</Heading>
                        <div class="tio-row-loose text-sm tio-fg-muted mb-3">
                          <span class="tio-row-extra-tight">
                            <PhBriefcase @size={{14}} />
                            {{job.department}}
                          </span>
                          <span class="tio-row-extra-tight">
                            <PhMapPin @size={{14}} />
                            {{job.location}}
                          </span>
                          <span class="tio-row-extra-tight">
                            <PhClock @size={{14}} />
                            {{job.posted}}
                          </span>
                        </div>
                        <div class="tio-row-tight">
                          <Chip @intent="success">
                            <span class="tio-inline-row gap-1">
                              <PhCheckCircle @size={{14}} />
                              92% Match
                            </span>
                          </Chip>
                          <Text @variant="muted" @size="sm">{{job.applicants}} applicants</Text>
                        </div>
                      </div>
                      <div class="tio-flex-tight">
                        <Button @intent="primary" @appearance="outlined">
                          Learn More
                        </Button>
                        <Button @intent="primary">
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  </div>
                {{/each}}
              </div>

              <div class="text-center py-6">
                <Text @variant="muted" class="mb-3">Don't see the right fit?</Text>
                <Button @intent="default" @appearance="outlined">
                  View All Roles ({{this.roleData.demand.openingsLocal}}
                  in your area)
                </Button>
              </div>
            </div>
          {{/if}}
        </div>
      </div>
    </VStack>
  </template>
}
